import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

/**
 * Aviso breve de confirmación: *Aplicado al plan*, *Copiado*, *Ajustes guardados* (sección 8.3).
 *
 * POR QUÉ NO ROBA EL FOCO
 *   Un toast confirma algo que el usuario acaba de hacer; si le moviera el foco le rompería el
 *   recorrido de teclado justo después de acertar. Se anuncia con `aria-live="polite"`, que lo
 *   lee cuando el lector termine la frase en curso, y desaparece solo.
 *
 * POR QUÉ SOLO HAY UNO A LA VEZ
 *   Una pila de avisos tapa la barra de pestañas y obliga a leer en orden inverso. El último
 *   mensaje sustituye al anterior: es el que corresponde a la última acción.
 */

const DURACION_MS = 4_000;

interface ToastContextValue {
  showToast: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const contexto = useContext(ToastContext);
  if (!contexto) {
    throw new Error("useToast necesita estar dentro de <ToastProvider>.");
  }
  return contexto;
}

interface ToastState {
  /** Cambia con cada aviso para que React reinicie la animación aunque el texto se repita. */
  id: number;
  message: string;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastState | null>(null);
  const temporizador = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = useCallback((message: string) => {
    if (temporizador.current !== null) clearTimeout(temporizador.current);
    setToast({ id: Date.now(), message });
    temporizador.current = setTimeout(() => setToast(null), DURACION_MS);
  }, []);

  useEffect(
    () => () => {
      if (temporizador.current !== null) clearTimeout(temporizador.current);
    },
    [],
  );

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* La región vive siempre en el árbol: si apareciera con el mensaje, el lector de
          pantalla no llegaría a anunciarlo. */}
      <div
        role="status"
        aria-live="polite"
        className="toast-dock pointer-events-none fixed inset-x-0 z-40 flex justify-center px-4"
      >
        {toast ? (
          <div
            key={toast.id}
            className="pointer-events-auto flex max-w-content items-center gap-2.5 rounded-[10px] bg-ink px-3.5 py-2.5 font-semibold text-paper"
          >
            <span aria-hidden="true" className="text-grass">
              ✔
            </span>
            <span>{toast.message}</span>
          </div>
        ) : null}
      </div>
    </ToastContext.Provider>
  );
}
